import { X, Users, Droplets, Weight, Clock, Ruler, ArrowUp, Check, Star, Sparkles, Plus, Tent, Trophy, Share2 } from "lucide-react";
import { useState } from "react";
import { ShopLinks } from "@/components/ShopLinks";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";
import type { Zelt, ZeltTag } from "@/data/zelte";
import { kategorien } from "@/data/zelte";

interface VergleichProps {
  zelte: Zelt[];
  onRemove: (id: string) => void;
  onNavigate: (path: string) => void;
  onClear?: () => void;
}

const tagLabels: Record<ZeltTag, string> = {
  neu: "Neu",
  bestseller: "Bestseller",
  sale: "Sale",
  empfohlen: "Empfohlen",
  leicht: "Ultraleicht",
  premium: "Premium",
};

interface CompareRowProps {
  label: string;
  icon?: React.ReactNode;
  values: (string | number | React.ReactNode)[];
  highlight?: "min" | "max";
  unit?: string;
}

function CompareRow({ label, icon, values, highlight, unit }: CompareRowProps) {
  const numericValues = values.map(v => (typeof v === "number" ? v : null));
  const hasNumeric = numericValues.some(v => v !== null);

  let bestIndex = -1;
  if (hasNumeric && highlight) {
    const filtered = numericValues.filter(v => v !== null) as number[];
    if (filtered.length > 0) {
      const best = highlight === "min" ? Math.min(...filtered) : Math.max(...filtered);
      bestIndex = numericValues.findIndex(v => v === best);
    }
  }

  return (
    <tr className="border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors">
      <td className="py-4 pr-6 text-sm font-medium whitespace-nowrap">
        <span className="flex items-center gap-2 text-muted-foreground">
          {icon}
          {label}
        </span>
      </td>
      {values.map((value, i) => (
        <td key={i} className="py-4 px-4 text-center">
          <span className={cn(
            "inline-flex items-center gap-1 text-sm",
            bestIndex === i && "font-bold text-green-600 dark:text-green-400"
          )}>
            {bestIndex === i && <Trophy className="w-3.5 h-3.5" />}
            {typeof value === "number" ? value : value}
            {unit && typeof value === "number" && <span className="text-muted-foreground text-xs">{unit}</span>}
          </span>
        </td>
      ))}
    </tr>
  );
}

export function Vergleich({ zelte, onRemove, onNavigate, onClear }: VergleichProps) {
  const [copied, setCopied] = useState(false);

  const shareCompare = async () => {
    const url = `${window.location.origin}/vergleich?ids=${zelte.map(z => z.id).join(",")}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Vergleichslink kopieren:", url);
    }
  };
  if (zelte.length === 0) {
    return (
      <Container>
        <div className="py-20 text-center">
          <div className="relative w-24 h-24 mx-auto mb-8">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 animate-pulse" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Tent className="w-12 h-12 text-primary" />
            </div>
          </div>
          <h2 className="text-3xl font-bold mb-3">Keine Zelte zum Vergleichen</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
            Fügen Sie Zelte zum Vergleich hinzu, um sie nebeneinander zu betrachten.
          </p>
          <Button
            size="lg"
            onClick={() => onNavigate("/zelte")}
            className="h-12 px-6 rounded-xl btn-premium text-primary-foreground"
          >
            <span className="relative z-10 flex items-center">
              Zelte entdecken
              <Plus className="ml-2 h-5 w-5" />
            </span>
          </Button>
        </div>
      </Container>
    );
  }

  // Calculate best values for highlighting
  const bestValues = {
    preis: Math.min(...zelte.map(z => z.preis)),
    bewertung: Math.max(...zelte.map(z => z.bewertung)),
    gewicht: Math.min(...zelte.map(z => z.gewicht)),
    wassersaeule: Math.max(...zelte.map(z => z.wassersaeule)),
  };

  return (
    <Container>
      <div className="py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-3">
              <Sparkles className="w-4 h-4" />
              Produktvergleich
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Zelt-Vergleich</h1>
            <p className="text-lg text-muted-foreground">
              Vergleichen Sie bis zu 4 Zelte nebeneinander
            </p>
          </div>
          <div className="flex flex-wrap gap-2 self-start">
            <Button
              variant="outline"
              onClick={shareCompare}
              className="h-11 px-5 rounded-xl gap-2"
            >
              <Share2 className="w-4 h-4" />
              {copied ? "Link kopiert" : "Vergleich teilen"}
            </Button>
            {onClear && (
              <Button variant="ghost" onClick={onClear} className="h-11 px-5 rounded-xl">
                Leeren
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => onNavigate("/zelte")}
              className="h-11 px-5 rounded-xl gap-2"
            >
              <Plus className="w-4 h-4" />
              Zelt hinzufügen
            </Button>
          </div>
        </div>

        {/* Mobile Cards View */}
        <div className="lg:hidden space-y-4 mb-8">
          {zelte.map((zelt, index) => {
            const kategorieName = kategorien.find(k => k.id === zelt.kategorie)?.name || zelt.kategorie;
            const isBestPrice = zelt.preis === bestValues.preis;
            const isBestRating = zelt.bewertung === bestValues.bewertung;

            return (
              <Card
                key={zelt.id}
                className={cn(
                  "relative overflow-hidden border-0",
                  "bg-card/50 backdrop-blur-sm",
                  "shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)]",
                  "slide-up"
                )}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-3 right-3 z-10 h-8 w-8 rounded-lg bg-background/80 hover:bg-destructive hover:text-destructive-foreground"
                  onClick={() => onRemove(zelt.id)}
                >
                  <X className="h-4 w-4" />
                </Button>

                <CardContent className="p-5">
                  <div className="flex gap-4 mb-5">
                    <div className="w-24 h-24 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                      <img src={zelt.bilder[0]} alt={zelt.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                        {zelt.marke}
                      </span>
                      <h3 className="font-bold text-lg truncate">{zelt.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={cn(
                                "w-3.5 h-3.5",
                                i < Math.floor(zelt.bewertung)
                                  ? "fill-amber-400 text-amber-400"
                                  : "fill-muted text-muted"
                              )}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-muted-foreground">({zelt.bewertungAnzahl})</span>
                      </div>
                      <div className="flex items-baseline gap-2 mt-2">
                        <span className="text-2xl font-bold">{zelt.preis}€</span>
                        {isBestPrice && (
                          <span className="text-xs font-semibold text-green-600 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
                            Bester Preis
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm mb-5">
                    {[
                      { icon: Users, label: "Personen", value: zelt.personen },
                      { icon: Droplets, label: "Wassersäule", value: `${zelt.wassersaeule}mm` },
                      { icon: Weight, label: "Gewicht", value: `${zelt.gewicht}kg` },
                      { icon: Clock, label: "Aufbau", value: `${zelt.aufbauzeit} Min` },
                    ].map(({ icon: Icon, label, value }) => (
                      <div key={label} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">{label}:</span>
                        <span className="font-medium ml-auto">{value}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    className="w-full h-10 rounded-xl"
                    onClick={() => onNavigate(`/zelte/${zelt.slug}`)}
                  >
                    Details ansehen
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block">
          <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left py-6 px-6 w-48 bg-muted/30"></th>
                    {zelte.map((zelt, index) => {
                      const isBestPrice = zelt.preis === bestValues.preis;
                      const isBestRating = zelt.bewertung === bestValues.bewertung;

                      return (
                        <th key={zelt.id} className="px-6 py-6 text-center min-w-[240px]">
                          <div className="relative">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute -top-2 right-0 h-8 w-8 rounded-lg hover:bg-destructive hover:text-destructive-foreground transition-colors"
                              onClick={() => onRemove(zelt.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>

                            {/* Best badges */}
                            {(isBestPrice || isBestRating) && (
                              <div className="absolute -top-2 -left-2 flex flex-col gap-1">
                                {isBestPrice && (
                                  <Badge className="bg-green-500 text-white text-[10px] px-2 py-0.5">
                                    <Trophy className="w-3 h-3 mr-1" />
                                    Bester Preis
                                  </Badge>
                                )}
                                {isBestRating && (
                                  <Badge className="bg-amber-500 text-white text-[10px] px-2 py-0.5">
                                    <Star className="w-3 h-3 mr-1 fill-white" />
                                    Top-Bewertet
                                  </Badge>
                                )}
                              </div>
                            )}

                            {/* Product Image */}
                            <div className="relative w-36 h-36 mx-auto mb-4 rounded-xl bg-muted overflow-hidden group">
                              <div className="absolute inset-0 image-shine">
                                <img
                                  src={zelt.bilder[0]}
                                  alt={zelt.name}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                              </div>
                            </div>

                            {/* Tags */}
                            {zelt.tags.length > 0 && (
                              <div className="flex justify-center gap-1.5 mb-3">
                                {zelt.tags.slice(0, 2).map(tag => (
                                  <Badge key={tag} variant={tag as any} className="text-[10px] px-2 py-0.5">
                                    {tagLabels[tag]}
                                  </Badge>
                                ))}
                              </div>
                            )}

                            <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                              {zelt.marke}
                            </span>
                            <h3 className="font-bold text-lg mb-2 line-clamp-1">{zelt.name}</h3>

                            {/* Rating */}
                            <div className="flex justify-center items-center gap-2 mb-3">
                              <div className="flex items-center gap-0.5">
                                {Array.from({ length: 5 }).map((_, i) => (
                                  <Star
                                    key={i}
                                    className={cn(
                                      "w-4 h-4",
                                      i < Math.floor(zelt.bewertung)
                                        ? "fill-amber-400 text-amber-400"
                                        : "fill-muted text-muted"
                                    )}
                                  />
                                ))}
                              </div>
                              <span className="text-sm font-medium">{zelt.bewertung}</span>
                            </div>

                            {/* Price */}
                            <div className="flex items-baseline justify-center gap-2">
                              <span className="text-2xl font-bold">{zelt.preis}€</span>
                              {zelt.originalPreis && (
                                <span className="text-sm text-muted-foreground line-through">
                                  {zelt.originalPreis}€
                                </span>
                              )}
                            </div>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  <CompareRow
                    label="Kategorie"
                    icon={<Tent className="w-4 h-4" />}
                    values={zelte.map(z => kategorien.find(k => k.id === z.kategorie)?.name || z.kategorie)}
                  />
                  <CompareRow
                    label="Personen"
                    icon={<Users className="w-4 h-4" />}
                    values={zelte.map(z => z.personen)}
                    highlight="max"
                  />
                  <CompareRow
                    label="Saison"
                    icon={<Sparkles className="w-4 h-4" />}
                    values={zelte.map(z => `${z.saison}-Saison`)}
                  />
                  <CompareRow
                    label="Gewicht"
                    icon={<Weight className="w-4 h-4" />}
                    values={zelte.map(z => z.gewicht)}
                    highlight="min"
                    unit="kg"
                  />
                  <CompareRow
                    label="Wassersäule"
                    icon={<Droplets className="w-4 h-4" />}
                    values={zelte.map(z => z.wassersaeule)}
                    highlight="max"
                    unit="mm"
                  />
                  <CompareRow
                    label="Aufbauzeit"
                    icon={<Clock className="w-4 h-4" />}
                    values={zelte.map(z => z.aufbauzeit)}
                    highlight="min"
                    unit="Min"
                  />
                  <CompareRow
                    label="Packmaß"
                    icon={<Ruler className="w-4 h-4" />}
                    values={zelte.map(z => z.packmass)}
                  />
                  <CompareRow
                    label="Höhe"
                    icon={<ArrowUp className="w-4 h-4" />}
                    values={zelte.map(z => z.hoehe)}
                    highlight="max"
                    unit="cm"
                  />
                  <CompareRow
                    label="Bewertung"
                    icon={<Star className="w-4 h-4" />}
                    values={zelte.map(z => (
                      <span className="inline-flex items-center gap-1.5">
                        {z.bewertung.toFixed(1)}
                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      </span>
                    ))}
                  />
                  <CompareRow
                    label="Features"
                    icon={<Check className="w-4 h-4" />}
                    values={zelte.map(z => (
                      <ul className="text-left text-xs space-y-1.5">
                        {z.features.slice(0, 4).map((f, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="w-4 h-4 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="h-2.5 w-2.5 text-green-500" />
                            </span>
                            <span className="line-clamp-1">{f}</span>
                          </li>
                        ))}
                        {z.features.length > 4 && (
                          <li className="text-muted-foreground pl-6">+{z.features.length - 4} weitere</li>
                        )}
                      </ul>
                    ))}
                  />
                  <tr>
                    <td className="py-6 px-6 bg-muted/30"></td>
                    {zelte.map(zelt => (
                      <td key={zelt.id} className="py-6 px-6 text-center">
                        <div className="space-y-2">
                          <Button
                            className="w-full h-11 rounded-xl btn-premium text-primary-foreground"
                            onClick={() => onNavigate(`/zelte/${zelt.slug}`)}
                          >
                            <span className="relative z-10 flex items-center justify-center">
                              Details ansehen
                            </span>
                          </Button>
                          <ShopLinks zelt={zelt} size="full" />
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {zelte.length < 4 && (
          <div className="mt-10 text-center">
            <p className="text-muted-foreground mb-4">
              Sie können noch {4 - zelte.length} weitere Zelte hinzufügen
            </p>
            <Button
              variant="outline"
              onClick={() => onNavigate("/zelte")}
              className="h-11 px-5 rounded-xl gap-2"
            >
              <Plus className="w-4 h-4" />
              Weitere Zelte hinzufügen
            </Button>
          </div>
        )}
      </div>
    </Container>
  );
}
